import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// High-quality image rendering styles applied to all player images
const HQ_IMG_STYLE = {
  imageRendering: 'auto',
  WebkitBackfaceVisibility: 'hidden',
  backfaceVisibility: 'hidden',
  transform: 'translateZ(0)', // Force GPU rendering
};

export default function ThumbnailGenerator() {
  const [searchParams] = useSearchParams();
  const [record, setRecord] = useState({ wins: 0, losses: 0, pending: 0 });
  const thumbnailRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [resolvedImage, setResolvedImage] = useState('');
  
  // Get customizable params from URL
  const headline = searchParams.get('headline') || 'FREE PICKS';
  const subtext = searchParams.get('subtext') || 'NBA PLAYER PROPS';
  const player = searchParams.get('player') || '';
  const stat = searchParams.get('stat') || '';
  const style = searchParams.get('style') || '1';
  const imageParam = searchParams.get('image') || ''; // Player image URL or tempid:xxx
  const effect = searchParams.get('effect') || 'glow'; // Image effect
  const outputSize = searchParams.get('size') || 'youtube'; // Output size
  const logo = searchParams.get('logo') || ''; // Sponsor logo URL
  const logoPos = searchParams.get('logopos') || 'bottom-right'; // Logo position

  // Resolve image - fetch from server if it's a temp ID
  useEffect(() => {
    const resolveImage = async () => {
      if (!imageParam) {
        setResolvedImage('');
        return;
      }
      
      // Check if it's a temp server ID
      if (imageParam.startsWith('tempid:')) {
        const tempId = imageParam.replace('tempid:', '');
        try {
          const response = await axios.get(`${API}/temp-image/${tempId}`);
          setResolvedImage(response.data.image_data);
        } catch (error) {
          console.error('Failed to fetch temp image:', error);
          setResolvedImage('');
        }
      } else {
        // Regular URL, decode it
        setResolvedImage(decodeURIComponent(imageParam));
      }
    };
    resolveImage();
  }, [imageParam]);

  // Use resolved image for display
  const image = resolvedImage;

  // Get dimensions based on output size
  const getDimensions = () => {
    switch (outputSize) {
      case 'twitter':
        return { width: 1200, height: 675 };
      case 'website':
        return { width: 1200, height: 630 };
      case 'hd':
        return { width: 1920, height: 1080 };
      default: // youtube
        return { width: 1280, height: 720 };
    }
  };

  const dimensions = getDimensions();

  // Get logo position classes
  const getLogoPositionStyle = () => {
    switch (logoPos) {
      case 'bottom-left':
        return { bottom: '20px', left: '20px' };
      case 'top-right':
        return { top: '20px', right: '20px' };
      default: // bottom-right
        return { bottom: '20px', right: '20px' };
    }
  };

  // Get image effect style
  const getImageEffectStyle = () => {
    switch (effect) {
      case 'glow':
        return { filter: 'drop-shadow(0 0 30px rgba(74,222,128,0.5)) drop-shadow(0 0 60px rgba(74,222,128,0.3))' };
      case 'shadow':
        return { filter: 'drop-shadow(8px 8px 20px rgba(0,0,0,0.8))' };
      case 'intense':
        return { filter: 'drop-shadow(0 0 40px rgba(74,222,128,0.7)) drop-shadow(0 0 80px rgba(74,222,128,0.4)) contrast(1.1) saturate(1.2)' };
      default:
        return {};
    }
  };

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const response = await axios.get(`${API}/results/summary`);
        const { wins, losses, pushes } = response.data;
        setRecord({ wins, losses, pending: pushes });
      } catch (error) {
        console.error('Failed to fetch record:', error);
      }
    };
    fetchRecord();
  }, []);

  const winRate = record.wins + record.losses > 0 
    ? ((record.wins / (record.wins + record.losses)) * 100).toFixed(1)
    : '0';

  // Download thumbnail as high-quality image
  const downloadThumbnail = async (format = 'png') => {
    if (!thumbnailRef.current) return;
    
    setDownloading(true);
    try {
      // Wait for all images inside the thumbnail to fully load
      const images = thumbnailRef.current.querySelectorAll('img');
      await Promise.all(
        Array.from(images).map(img => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });
        })
      );

      const canvas = await html2canvas(thumbnailRef.current, {
        scale: 2, // 2x resolution for crisp, sharp output
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#000000',
        imageTimeout: 30000,
        logging: false,
        // Improve rendering quality
        onclone: (clonedDoc) => {
          // Force high-quality image rendering on cloned images
          const clonedImages = clonedDoc.querySelectorAll('img');
          clonedImages.forEach(img => {
            img.style.imageRendering = 'auto';
            img.crossOrigin = 'anonymous';
          });
        }
      });
      
      const link = document.createElement('a');
      const filename = `PSL-${player || 'thumbnail'}-${Date.now()}`;
      
      if (format === 'png') {
        // Lossless PNG — maximum quality, no artifacts
        link.download = `${filename}.png`;
        link.href = canvas.toDataURL('image/png');
      } else {
        // High-quality JPEG (95%) — for YouTube's 2MB limit
        link.download = `${filename}.jpg`;
        link.href = canvas.toDataURL('image/jpeg', 0.95);
      }
      link.click();
    } catch (error) {
      console.error('Failed to download:', error);
      alert('Failed to download. Try taking a screenshot instead.');
    }
    setDownloading(false);
  };

  // Wrapper component with download button
  const ThumbnailWrapper = ({ children }) => (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
      <div ref={thumbnailRef} style={{ imageRendering: 'auto' }}>
        {children}
      </div>
      <div className="mt-6 flex gap-4">
        <button
          onClick={() => downloadThumbnail('png')}
          disabled={downloading}
          className="px-8 py-3 bg-green-500 hover:bg-green-600 text-black font-bold text-lg rounded-lg shadow-lg transition-all disabled:opacity-50"
        >
          {downloading ? 'Generating...' : 'Download PNG (Best Quality)'}
        </button>
        <button
          onClick={() => downloadThumbnail('jpeg')}
          disabled={downloading}
          className="px-8 py-3 bg-blue-500 hover:bg-blue-600 text-white font-bold text-lg rounded-lg shadow-lg transition-all disabled:opacity-50"
        >
          {downloading ? 'Generating...' : 'Download JPG (YouTube)'}
        </button>
      </div>
      <p className="mt-2 text-gray-500 text-sm">PNG = lossless max quality | JPG = optimized for YouTube upload (&lt;2MB)</p>
    </div>
  );

  // Style 1 - Player CENTER, Stats on LEFT and RIGHT
  if (style === '1') {
    return (
      <ThumbnailWrapper>
      <div style={{ width: dimensions.width, height: dimensions.height }} className="bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        {/* Left Side - Stats & Branding */}
        <div className="absolute left-8 top-1/2 -translate-y-1/2 z-10">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-black font-bold text-lg">PSL</span>
            </div>
            <span className="text-white font-heading text-xl tracking-wider">PRIMESTATLAB</span>
          </div>
          
          {/* Main Headline */}
          <h1 className="text-5xl font-heading font-black leading-none mb-4">
            <span className="text-primary">{headline.split(' ')[0]}</span>
            <br />
            <span className="text-white">{headline.split(' ').slice(1).join(' ')}</span>
          </h1>
          
          {/* Two-line Tagline */}
          <p className="text-base text-gray-400 font-mono leading-relaxed">
            Data-driven picks.<br />No fluff, just stats.
          </p>
          
          {/* Player Name & Stat */}
          {player && (
            <div className="mt-4">
              <div className="text-2xl font-heading text-white">{player}</div>
              {stat && <div className="text-xl text-primary font-heading">{stat}</div>}
            </div>
          )}
        </div>

        {/* Center - Player Image - Positioned to fill from bottom, overflow top if needed */}
        {image && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-0 z-20" style={{ height: '115%' }}>
            <img 
              src={decodeURIComponent(image)} 
              alt="Player" 
              className="h-full w-auto object-contain"
              style={{ ...getImageEffectStyle(), ...HQ_IMG_STYLE, objectPosition: 'center bottom' }}
            />
          </div>
        )}

        {/* Right Side - Stats Card */}
        <div className="absolute right-8 top-1/2 -translate-y-1/2 z-10">
          <div className="bg-black/70 border-2 border-primary/50 p-6 backdrop-blur-sm">
            <div className="text-center mb-4">
              <div className="text-5xl font-heading font-black text-primary">{winRate}%</div>
              <div className="text-gray-400 font-mono uppercase tracking-widest text-xs">Win Rate</div>
            </div>
            <div className="flex justify-center gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-green-500">{record.wins}W</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-500">{record.losses}L</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-500">{record.pending}P</div>
              </div>
            </div>
          </div>
          
          {/* Subtext Badge */}
          <div className="mt-4 text-center">
            <span className="bg-primary text-black px-4 py-2 text-sm font-bold uppercase tracking-widest">
              {subtext}
            </span>
          </div>
        </div>

        {/* Sponsor Logo */}
        {logo && outputSize !== 'website' && (
          <div className="absolute z-30" style={getLogoPositionStyle()}>
            <img src={decodeURIComponent(logo)} alt="Sponsor" className="h-16 w-auto" />
          </div>
        )}
      </div>
      </ThumbnailWrapper>
    );
  }

  // Style 2 - Player CENTER, Stats BEHIND player offset to right, text above
  if (style === '2') {
    return (
      <ThumbnailWrapper>
      <div style={{ width: dimensions.width, height: dimensions.height }} className="bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        {/* FREE PICKS Badge - Top Left */}
        <div className="absolute top-5 left-5 z-30">
          <span className="bg-primary text-black px-4 py-2 text-lg font-black uppercase tracking-wider" style={{ boxShadow: '0 4px 20px rgba(74,222,128,0.5)' }}>
            FREE PICKS
          </span>
        </div>
        
        {/* Top Right - Logo & Branding */}
        <div className="absolute top-5 right-5 z-30 text-right">
          <div className="flex items-center justify-end gap-2">
            <span className="text-white font-heading text-lg tracking-wider">PRIMESTATLAB</span>
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-black font-bold text-lg">PSL</span>
            </div>
          </div>
        </div>

        {/* Above Player - Day Badge & Headline */}
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-30 text-center">
          <div className="mb-3">
            <span className="bg-primary text-black px-5 py-2 text-xl font-bold uppercase tracking-widest">
              {subtext}
            </span>
          </div>
          {player && (
            <div>
              <h1 className="text-4xl font-heading font-black text-white">{player}</h1>
              {stat && <div className="text-2xl text-primary font-heading mt-1">{stat}</div>}
            </div>
          )}
        </div>

        {/* Stats BEHIND Player - Offset to the RIGHT so player partially covers */}
        <div className="absolute left-1/2 top-1/2 -translate-y-1/2 z-10" style={{ marginLeft: '120px' }}>
          <div className="bg-black/80 border-2 border-primary/50 p-8 backdrop-blur-sm">
            <div className="text-center mb-4">
              <div className="text-6xl font-heading font-black text-primary">{winRate}%</div>
              <div className="text-gray-400 font-mono uppercase tracking-widest text-sm">Win Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white">{record.wins}-{record.losses}</div>
              <div className="text-gray-400 font-mono uppercase tracking-widest text-xs mt-1">Record</div>
            </div>
          </div>
        </div>

        {/* Center - Player Image (covers part of stats) - Fills from bottom */}
        {image && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-0 z-20" style={{ height: '120%' }}>
            <img 
              src={decodeURIComponent(image)} 
              alt="Player" 
              className="h-full w-auto object-contain"
              style={{ ...getImageEffectStyle(), ...HQ_IMG_STYLE, objectPosition: 'center bottom' }}
            />
          </div>
        )}

        {/* Bottom - Tagline */}
        <div className="absolute bottom-5 left-5 z-30">
          <p className="text-sm text-gray-400 font-mono leading-relaxed">
            Data-driven picks.<br />No fluff, just stats.
          </p>
        </div>

        {/* Sponsor Logo */}
        {logo && outputSize !== 'website' && (
          <div className="absolute z-30" style={getLogoPositionStyle()}>
            <img src={decodeURIComponent(logo)} alt="Sponsor" className="h-16 w-auto" />
          </div>
        )}
      </div>
      </ThumbnailWrapper>
    );
  }

  // Style 6 - Top Banner (all text at top)
  if (style === '6') {
    return (
      <ThumbnailWrapper>
      <div style={{ width: dimensions.width, height: dimensions.height }} className="bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
        {/* Top Banner - All Text */}
        <div className="absolute top-0 left-0 right-0 bg-black/80 px-8 py-4 z-30 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="bg-primary text-black px-4 py-2 text-lg font-bold uppercase">{subtext}</span>
            <h1 className="text-4xl font-heading font-black">
              <span className="text-primary">{headline.split(' ')[0]}</span>
              <span className="text-white"> {headline.split(' ').slice(1).join(' ')}</span>
            </h1>
            {player && <span className="text-2xl text-white font-heading">{player}</span>}
            {stat && <span className="text-xl text-primary">{stat}</span>}
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="text-3xl font-black text-primary">{winRate}%</div>
              <div className="text-xs text-gray-400">WIN RATE</div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-black text-white">{record.wins}-{record.losses}</div>
              <div className="text-xs text-gray-400">RECORD</div>
            </div>
            <div className="w-10 h-10 bg-primary rounded flex items-center justify-center">
              <span className="text-black font-bold">PSL</span>
            </div>
          </div>
        </div>

        {/* Player Image - Fills rest */}
        {image && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-0 z-10" style={{ height: '95%' }}>
            <img src={decodeURIComponent(image)} alt="Player" className="h-full w-auto object-contain object-bottom" style={{ ...getImageEffectStyle(), ...HQ_IMG_STYLE }} />
          </div>
        )}

        {/* Sponsor Logo */}
        {logo && outputSize !== 'website' && (
          <div className="absolute bottom-5 right-5 z-30">
            <img src={decodeURIComponent(logo)} alt="Sponsor" className="h-14 w-auto" />
          </div>
        )}
      </div>
      </ThumbnailWrapper>
    );
  }

  // Style 7 - Bottom Strip (text at bottom)
  if (style === '7') {
    return (
      <ThumbnailWrapper>
      <div style={{ width: dimensions.width, height: dimensions.height }} className="bg-gradient-to-t from-black via-gray-900 to-black relative overflow-hidden">
        {/* Player Image - Fills top */}
        {image && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-0 z-10" style={{ height: '100%' }}>
            <img src={decodeURIComponent(image)} alt="Player" className="h-full w-auto object-contain object-bottom" style={{ ...getImageEffectStyle(), ...HQ_IMG_STYLE }} />
          </div>
        )}

        {/* Bottom Strip - All Text */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/95 to-transparent px-8 py-6 z-30">
          <div className="flex items-end justify-between">
            <div>
              <span className="bg-primary text-black px-3 py-1 text-sm font-bold uppercase">{subtext}</span>
              <h1 className="text-5xl font-heading font-black mt-2">
                <span className="text-primary">{headline.split(' ')[0]}</span>
                <span className="text-white"> {headline.split(' ').slice(1).join(' ')}</span>
              </h1>
              {player && <div className="text-2xl text-white font-heading mt-1">{player} {stat && <span className="text-primary">{stat}</span>}</div>}
            </div>
            <div className="flex items-center gap-6">
              <div className="bg-black/50 border border-primary/30 px-4 py-2 text-center">
                <div className="text-3xl font-black text-primary">{winRate}%</div>
                <div className="text-xs text-gray-400">WIN RATE</div>
              </div>
              <div className="bg-black/50 border border-primary/30 px-4 py-2 text-center">
                <div className="text-3xl font-black text-white">{record.wins}-{record.losses}</div>
                <div className="text-xs text-gray-400">RECORD</div>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-10 h-10 bg-primary rounded flex items-center justify-center">
                  <span className="text-black font-bold">PSL</span>
                </div>
                <span className="text-[10px] text-gray-400">PRIMESTATLAB</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sponsor Logo */}
        {logo && outputSize !== 'website' && (
          <div className="absolute top-5 right-5 z-30">
            <img src={decodeURIComponent(logo)} alt="Sponsor" className="h-14 w-auto" />
          </div>
        )}
      </div>
      </ThumbnailWrapper>
    );
  }

  // Style 8 - BILL STYLE - Half screen text, half screen player
  if (style === '8') {
    return (
      <ThumbnailWrapper>
      <div style={{ width: dimensions.width, height: dimensions.height }} className="bg-black relative overflow-hidden flex">
        {/* Player Image - BIG, spans across */}
        {image && (
          <div className="absolute z-10" style={{ height: '115%', bottom: '-80px', left: '-120px' }}>
            <img src={decodeURIComponent(image)} alt="Player" className="h-full w-auto object-contain object-bottom" style={{ ...getImageEffectStyle(), ...HQ_IMG_STYLE }} />
          </div>
        )}

        {/* LEFT SIDE - Player info overlay - 65% */}
        <div className="w-[65%] h-full relative z-20">
          {/* Player Name & Stats - Top Left */}
          <div className="absolute top-4 left-4">
            {player && <div className="text-4xl font-heading font-black text-white">{player}</div>}
            {stat && <div className="text-2xl text-primary font-heading font-bold">{stat}</div>}
            <div className="flex items-center gap-3 mt-2">
              <span className="text-3xl font-black text-primary">{winRate}%</span>
              <span className="text-2xl font-bold text-white">{record.wins}-{record.losses}</span>
            </div>
          </div>
          
          {/* PSL Logo - Bottom Left */}
          <div className="absolute bottom-4 left-4">
            <div className="w-12 h-12 bg-primary rounded flex items-center justify-center">
              <span className="text-black font-bold text-lg">PSL</span>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - Text fills this side - 35%, CENTERED */}
        <div className="w-[35%] h-full bg-primary flex flex-col justify-center items-center relative z-20">
          {/* NBA THURSDAY - top center, BIGGER */}
          <div className="absolute top-4 left-0 right-0 text-center">
            <span className="text-black text-4xl font-black uppercase tracking-wide">
              {subtext}
            </span>
          </div>

          {/* FREE PICKS - Stacked, CENTERED */}
          <div className="text-center">
            <h1 className="font-heading font-black text-black leading-[0.85]" style={{ fontSize: '180px' }}>
              {headline.split(' ')[0]}
            </h1>
            <h1 className="font-heading font-black text-white leading-[0.85]" style={{ fontSize: '180px', textShadow: '5px 5px 0px rgba(0,0,0,0.3)' }}>
              {headline.split(' ').slice(1).join(' ')}
            </h1>
          </div>

          {/* PrizePicks - Bottom center */}
          {logo && outputSize !== 'website' && (
            <div className="absolute bottom-4 left-0 right-0 flex justify-center z-30">
              <img src={decodeURIComponent(logo)} alt="Sponsor" className="h-14 w-auto" />
            </div>
          )}
        </div>
      </div>
      </ThumbnailWrapper>
    );
  }

  // Style 5 - Player on LEFT, text on RIGHT edge
  if (style === '5') {
    return (
      <ThumbnailWrapper>
      <div style={{ width: dimensions.width, height: dimensions.height }} className="bg-gradient-to-r from-black via-gray-900 to-black relative overflow-hidden">
        {/* FREE PICKS Badge - Top Right */}
        <div className="absolute top-5 right-5 z-30">
          <span className="bg-primary text-black px-4 py-2 text-lg font-black uppercase tracking-wider" style={{ boxShadow: '0 4px 20px rgba(74,222,128,0.5)' }}>
            FREE PICKS
          </span>
        </div>

        {/* Large Player Image - Left Side */}
        {image && (
          <div className="absolute left-0 bottom-0 h-full flex items-end z-10">
            <img 
              src={decodeURIComponent(image)} 
              alt="Player" 
              className="h-[110%] w-auto object-contain object-bottom"
              style={{ ...getImageEffectStyle(), ...HQ_IMG_STYLE }}
            />
          </div>
        )}

        {/* Right Side Content - All the way to the right edge */}
        <div className="absolute right-0 top-0 h-full w-[50%] flex flex-col justify-center items-end pr-8 text-right z-20">
          {/* Top Badge */}
          <div className="mb-4">
            <span className="bg-primary text-black px-4 py-2 text-sm font-bold uppercase tracking-widest">
              {subtext}
            </span>
          </div>

          {/* Player Name Large */}
          {player && (
            <h1 className="text-6xl font-heading font-black text-white leading-tight mb-2 text-right">
              {player}
            </h1>
          )}

          {/* Stat Line */}
          {stat && (
            <div className="text-4xl font-heading text-primary mb-6 text-right">
              {stat}
            </div>
          )}

          {/* Headline - With Text Glow Effect */}
          <h2 className="text-5xl font-heading font-black mb-6 text-right" style={{ textShadow: '0 0 30px rgba(74,222,128,0.8), 0 0 60px rgba(74,222,128,0.5), 3px 3px 6px rgba(0,0,0,0.9)' }}>
            <span className="text-primary">{headline.split(' ')[0]}</span>
            <span className="text-white" style={{ textShadow: '0 0 20px rgba(255,255,255,0.5), 3px 3px 6px rgba(0,0,0,0.9)' }}> {headline.split(' ').slice(1).join(' ')}</span>
          </h2>

          {/* Stats */}
          <div className="flex gap-8">
            <div className="bg-black/50 border border-primary/30 px-6 py-3">
              <div className="text-3xl font-black text-primary">{winRate}%</div>
              <div className="text-gray-500 font-mono text-xs uppercase">Win Rate</div>
            </div>
            <div className="bg-black/50 border border-primary/30 px-6 py-3">
              <div className="text-3xl font-black text-white">{record.wins}-{record.losses}</div>
              <div className="text-gray-500 font-mono text-xs uppercase">Record</div>
            </div>
          </div>

          {/* Logo */}
          <div className="mt-8 flex items-center gap-2">
            <span className="text-gray-400 font-mono text-sm">PRIMESTATLAB.COM</span>
            <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
              <span className="text-black font-bold text-sm">PSL</span>
            </div>
          </div>
        </div>

        {/* Sponsor Logo - Bottom LEFT */}
        {logo && outputSize !== 'website' && (
          <div className="absolute bottom-5 left-5 z-30">
            <img src={decodeURIComponent(logo)} alt="Sponsor" className="h-16 w-auto" />
          </div>
        )}
      </div>
      </ThumbnailWrapper>
    );
  }

  // Style 4 - Player on RIGHT, text on LEFT (mirror of Style 3)
  if (style === '4') {
    return (
      <ThumbnailWrapper>
      <div style={{ width: dimensions.width, height: dimensions.height }} className="bg-gradient-to-l from-black via-gray-900 to-black relative overflow-hidden">
        {/* FREE PICKS Badge - Always Visible Top Right */}
        <div className="absolute top-5 right-5 z-30">
          <span className="bg-primary text-black px-4 py-2 text-lg font-black uppercase tracking-wider" style={{ boxShadow: '0 4px 20px rgba(74,222,128,0.5)' }}>
            FREE PICKS
          </span>
        </div>

        {/* Large Player Image - Right Side */}
        {image && (
          <div className="absolute right-0 bottom-0 h-full flex items-end z-10">
            <img 
              src={decodeURIComponent(image)} 
              alt="Player" 
              className="h-[110%] w-auto object-contain object-bottom"
              style={{ ...getImageEffectStyle(), ...HQ_IMG_STYLE }}
            />
          </div>
        )}

        {/* Left Side Content */}
        <div className="absolute left-0 top-0 h-full w-[55%] flex flex-col justify-center pl-12 z-20">
          {/* Top Badge */}
          <div className="mb-4">
            <span className="bg-primary text-black px-4 py-2 text-sm font-bold uppercase tracking-widest">
              {subtext}
            </span>
          </div>

          {/* Player Name Large */}
          {player && (
            <h1 className="text-6xl font-heading font-black text-white leading-tight mb-2">
              {player}
            </h1>
          )}

          {/* Stat Line */}
          {stat && (
            <div className="text-4xl font-heading text-primary mb-6">
              {stat}
            </div>
          )}

          {/* Headline - With Text Glow Effect */}
          <h2 className="text-5xl font-heading font-black mb-6" style={{ textShadow: '0 0 30px rgba(74,222,128,0.8), 0 0 60px rgba(74,222,128,0.5), 3px 3px 6px rgba(0,0,0,0.9)' }}>
            <span className="text-primary">{headline.split(' ')[0]}</span>
            <span className="text-white" style={{ textShadow: '0 0 20px rgba(255,255,255,0.5), 3px 3px 6px rgba(0,0,0,0.9)' }}> {headline.split(' ').slice(1).join(' ')}</span>
          </h2>

          {/* Stats */}
          <div className="flex gap-8">
            <div className="bg-black/50 border border-primary/30 px-6 py-3">
              <div className="text-3xl font-black text-primary">{winRate}%</div>
              <div className="text-gray-500 font-mono text-xs uppercase">Win Rate</div>
            </div>
            <div className="bg-black/50 border border-primary/30 px-6 py-3">
              <div className="text-3xl font-black text-white">{record.wins}-{record.losses}</div>
              <div className="text-gray-500 font-mono text-xs uppercase">Record</div>
            </div>
          </div>

          {/* Logo */}
          <div className="mt-8 flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
              <span className="text-black font-bold text-sm">PSL</span>
            </div>
            <span className="text-gray-400 font-mono text-sm">PRIMESTATLAB.COM</span>
          </div>
        </div>

        {/* Sponsor Logo */}
        {logo && outputSize !== 'website' && (
          <div className="absolute z-30" style={getLogoPositionStyle()}>
            <img src={decodeURIComponent(logo)} alt="Sponsor" className="h-16 w-auto" />
          </div>
        )}
      </div>
      </ThumbnailWrapper>
    );
  }

  // Style 3 - Player Focused (large image, text overlay)
  return (
    <ThumbnailWrapper>
    <div style={{ width: dimensions.width, height: dimensions.height }} className="bg-gradient-to-r from-black via-gray-900 to-black relative overflow-hidden">
      {/* FREE PICKS Badge - Always Visible Top Left */}
      <div className="absolute top-5 left-5 z-30">
        <span className="bg-primary text-black px-4 py-2 text-lg font-black uppercase tracking-wider" style={{ boxShadow: '0 4px 20px rgba(74,222,128,0.5)' }}>
          FREE PICKS
        </span>
      </div>

      {/* Large Player Image - Center/Left */}
      {image && (
        <div className="absolute left-0 bottom-0 h-full flex items-end z-10">
          <img 
            src={decodeURIComponent(image)} 
            alt="Player" 
            className="h-[110%] w-auto object-contain object-bottom"
            style={{ ...getImageEffectStyle(), ...HQ_IMG_STYLE }}
          />
        </div>
      )}

      {/* Right Side Content */}
      <div className="absolute right-0 top-0 h-full w-[55%] flex flex-col justify-center pr-12 z-20">
        {/* Top Badge */}
        <div className="mb-4">
          <span className="bg-primary text-black px-4 py-2 text-sm font-bold uppercase tracking-widest">
            {subtext}
          </span>
        </div>

        {/* Player Name Large */}
        {player && (
          <h1 className="text-6xl font-heading font-black text-white leading-tight mb-2">
            {player}
          </h1>
        )}

        {/* Stat Line */}
        {stat && (
          <div className="text-4xl font-heading text-primary mb-6">
            {stat}
          </div>
        )}

        {/* Headline - With Text Glow Effect */}
        <h2 className="text-5xl font-heading font-black mb-6" style={{ textShadow: '0 0 30px rgba(74,222,128,0.8), 0 0 60px rgba(74,222,128,0.5), 3px 3px 6px rgba(0,0,0,0.9)' }}>
          <span className="text-primary">{headline.split(' ')[0]}</span>
          <span className="text-white" style={{ textShadow: '0 0 20px rgba(255,255,255,0.5), 3px 3px 6px rgba(0,0,0,0.9)' }}> {headline.split(' ').slice(1).join(' ')}</span>
        </h2>

        {/* Stats */}
        <div className="flex gap-8">
          <div className="bg-black/50 border border-primary/30 px-6 py-3">
            <div className="text-3xl font-black text-primary">{winRate}%</div>
            <div className="text-gray-500 font-mono text-xs uppercase">Win Rate</div>
          </div>
          <div className="bg-black/50 border border-primary/30 px-6 py-3">
            <div className="text-3xl font-black text-white">{record.wins}-{record.losses}</div>
            <div className="text-gray-500 font-mono text-xs uppercase">Record</div>
          </div>
        </div>

        {/* Logo */}
        <div className="mt-8 flex items-center gap-2">
          <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
            <span className="text-black font-bold text-sm">PSL</span>
          </div>
          <span className="text-gray-400 font-mono text-sm">PRIMESTATLAB.COM</span>
        </div>
      </div>

      {/* Sponsor Logo */}
      {logo && outputSize !== 'website' && (
        <div className="absolute z-30" style={getLogoPositionStyle()}>
          <img src={decodeURIComponent(logo)} alt="Sponsor" className="h-16 w-auto" />
        </div>
      )}
    </div>
    </ThumbnailWrapper>
  );
}
